import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Popover, Select, Slider, Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PublishIcon from '@material-ui/icons/Publish';
import SaveIcon from '@material-ui/icons/Save';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  changeMerchandisingRuleStatus,
  clearPreview,
  clearRuleEditorFeedback,
  deleteMerchandisingRule,
  fetchMerchandisingRule,
  fetchMerchandisingRules,
  fetchMerchandisingIndexState,
  runMerchandisingPreview,
  saveMerchandisingRule,
  searchMerchandisingValues,
  selectActiveRulePage,
} from '../state/merchandisingSlice';
import { resolveProducts, searchProducts } from '../../../slices/ProductSelectorSlice';
import SchemaValueSelector from './SchemaValueSelector';
import {
  appendRankingCondition,
  defaultConditionValue,
  emptyRankingWorkbench,
  groupRankingConditions,
  rankingExpressionValid,
  rankingSearchTermsValid,
  rankingRuleToWorkbench,
  rankingWorkbenchRule,
  removeRankingCondition,
  ungroupRankingConditions,
  updateRankingCondition,
} from '../model/rankingWorkbench';
import {
  formatLocalDateTime, InlineNotice, PageHeader, PriorityLabel, ProductImage, ScheduleCard,
  SearchInput, SelectionCheckbox, StateBoundary, StatusChip, WorkbenchPagination,
} from './MerchandisingWorkbenchUi';
import RuleTransferControls from './RuleTransferControls';
import IndexStatusBar from './IndexStatusBar';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from './UnsavedChangesGuard';
import { CapabilityNotice } from './MerchandisingStatus';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useMerchandisingWorkbenchCopy } from './useMerchandisingWorkbenchCopy';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import SearchPhraseEditor from './SearchPhraseEditor';
import {
  rankingFactorMarks, rankingFactorToPosition, rankingPositionToFactor,
} from '../model/rankingFactorScale';
import { merchandisingRegexError } from '../model/regexValidation';
import {
  conditionOperatorAvailable,
  NUMERIC_COMPARISON_OPERATORS as numericComparisonOperators,
  REGEX_OPERATORS as regexOperators,
  ruleUsesAdvancedConditions,
} from '../model/ruleCapabilities';
import { runWithConcurrency } from '../model/rulePortability';

const validSchedule = (from, to) => {
  if (from && Number.isNaN(Date.parse(from))) return false;
  if (to && Number.isNaN(Date.parse(to))) return false;
  return !(from && to) || Date.parse(to) > Date.parse(from);
};
const multipleOperators = new Set([
  'in', 'not_in', 'contains_any', 'contains_all', 'contains_none',
  'text_contains_any', 'text_not_contains_any', 'text_contains_any_case',
]);
const numericOperators = ['eq', 'gt', 'gte', 'lt', 'lte', 'between'];
export const descriptorForCapabilities = (descriptor, advancedConditions) => ({
  ...descriptor,
  operators: (descriptor?.operators || []).filter(operator => (
    conditionOperatorAvailable(operator, descriptor, advancedConditions)
  )),
});
const isNumericCondition = (condition, descriptor) => descriptor?.value_type === 'number'
  || numericComparisonOperators.has(condition.operator);
export const preferredOperator = (descriptor) => (
  (descriptor?.value_type === 'number' && descriptor?.operators?.includes('between') ? 'between' : null)
  ||
  descriptor?.operators?.find((operator) => operator === 'in' || operator === 'contains_any')
  || descriptor?.operators?.[0]
);

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
  });
};

const fieldLabels = {
  available: 'Availability',
  brand_id: 'Brand',
  category_ancestor_ids: 'Category subtree',
  category_ids: 'Category',
  category_path_names: 'Category path names',
  category_path_text: 'Category path text',
  group_id: 'Product group',
  normalized_product_name: 'Product type and name',
  primary_category_name: 'Primary category name',
  product_name: 'Product name',
  product_type: 'Product Type',
  vendor_name: 'Vendor name',
  price: 'Price',
  primary_category_id: 'Primary category',
  product_id: 'Product',
  stock_quantity: 'Stock quantity',
};
const humanField = (field, descriptor = null) => descriptor?.label
  || descriptor?.value_source?.label
  || fieldLabels[field]
  || field.replace(/^attributes\.feature[:.]?/, 'Feature #')
    .replace(/^attributes\./, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
export const descriptorForSavedCondition = (condition, schema, advancedConditions = true) => {
  const savedAttributeLabel = condition.field.startsWith('attributes.')
    ? condition.field.slice('attributes.'.length)
    : null;
  const labelMatches = savedAttributeLabel
    ? Object.values(schema).filter(candidate => (
      candidate?.key?.startsWith('attributes.')
      && [candidate.label, candidate.value_source?.label].includes(savedAttributeLabel)
    )) : [];
  // A translated display label is only a safe legacy identity when unique.
  // Never let object iteration order silently retarget an imported rule.
  const descriptor = schema[condition.field]
    || (labelMatches.length === 1 ? labelMatches[0] : null);
  if (descriptor) {
    const supported = (descriptor.operators || []).filter(operator => (
      conditionOperatorAvailable(operator, descriptor, advancedConditions)
    ));
    if (supported.length === (descriptor.operators || []).length
      && supported.includes(condition.operator)) return descriptor;
    return supported.includes(condition.operator)
      ? { ...descriptor, operators: supported }
      : { ...descriptor, operators: [condition.operator, ...supported] };
  }
  // Saved rules can target catalog attributes that are not configured as facets.
  const numeric = numericComparisonOperators.has(condition.operator);
  return {
    label: humanField(condition.field),
    value_type: 'scalar_or_list',
    operators: advancedConditions && numeric && condition.field.startsWith('attributes.')
      ? (numericOperators.includes(condition.operator)
        ? numericOperators : [condition.operator, ...numericOperators])
      : [condition.operator],
  };
};
export const canonicalizeSavedConditionFields = (group, schema) => ({
  ...group,
  items: (group?.items || []).map((item) => {
    if (Array.isArray(item.items)) return canonicalizeSavedConditionFields(item, schema);
    const descriptor = descriptorForSavedCondition(item, schema);
    return descriptor?.key && descriptor.key !== item.field
      ? { ...item, field: descriptor.key }
      : item;
  }),
});
const operatorLabels = {
  between: 'is between',
  contains_all: 'contains all',
  contains_any: 'contains one of',
  contains_none: 'contains none',
  text_contains_any: 'contains text',
  text_not_contains_any: 'does not contain text',
  // Keep old rules readable without advertising the implementation-specific legacy mode.
  text_contains_any_case: 'contains text',
  eq: 'is',
  gt: 'is greater than (>)',
  gte: 'is at least (≥)',
  in: 'is one of',
  lt: 'is less than (<)',
  lte: 'is at most (≤)',
  matches_regex: 'matches regular expression',
  neq: 'is not',
  not_in: 'is not one of',
  not_matches_regex: 'does not match regular expression',
};
const humanOperator = (operator) => operatorLabels[operator] || operator.replace(/_/g, ' ');
const operatorGroup = (operator) => {
  if (numericComparisonOperators.has(operator)) return 'number';
  if (regexOperators.has(operator) || operator.startsWith('text_')) return 'text';
  if (['in', 'not_in', 'contains_any', 'contains_all', 'contains_none'].includes(operator)) {
    return 'list';
  }
  return 'value';
};
const conditionOperatorOptions = (descriptor, currentOperator, t, advancedConditions) => (descriptor?.operators || [])
  .filter((operator) => {
    if (!conditionOperatorAvailable(operator, descriptor, advancedConditions)
      && operator !== currentOperator) return false;
    if (currentOperator === 'text_contains_any_case') return operator !== 'text_contains_any';
    return operator !== 'text_contains_any_case';
  })
  .flatMap(operator => (operator === 'exists' ? [
    { value: 'exists:true', label: t('merchandising.condition.hasValue'), group: 'value' },
    { value: 'exists:false', label: t('merchandising.condition.hasNoValue'), group: 'value' },
  ] : [{ value: operator, label: humanOperator(operator), group: operatorGroup(operator) }]));

const operatorGroupOrder = ['value', 'list', 'text', 'number'];
export const groupConditionOperatorOptions = options => operatorGroupOrder
  .map(group => ({ group, options: options.filter(option => option.group === group) }))
  .filter(entry => entry.options.length);

export const conditionValueForOperatorChange = (condition, descriptor, selection) => {
  const operator = selection.startsWith('exists:') ? 'exists' : selection;
  if (operator === 'exists') return selection === 'exists:true';
  if (condition.operator === 'exists') return defaultConditionValue(descriptor, operator);

  const wasMultiple = multipleOperators.has(condition.operator);
  const nextMultiple = multipleOperators.has(operator);
  const current = condition.value;
  if (regexOperators.has(operator)) {
    const seed = Array.isArray(current) ? current[0] : current;
    return typeof seed === 'string' ? seed : (seed == null ? '' : String(seed));
  }
  if (operator === 'between') {
    if (condition.operator === 'between' && Array.isArray(current) && current.length === 2) {
      return current;
    }
    const seed = Array.isArray(current) ? current[0] : current;
    const number = Number(seed);
    return seed === '' || seed == null || typeof seed === 'boolean' || !Number.isFinite(number)
      ? ['', ''] : [number, number];
  }
  if (numericComparisonOperators.has(operator)) {
    const seed = Array.isArray(current) ? current[0] : current;
    const number = Number(seed);
    return seed === '' || seed == null || typeof seed === 'boolean' || !Number.isFinite(number)
      ? '' : number;
  }
  if (condition.operator === 'between') {
    const first = Array.isArray(current) ? current[0] : current;
    if (nextMultiple) return first === '' || first == null ? [] : [String(first)];
    return descriptor?.value_type === 'number' ? (first ?? '') : String(first ?? '');
  }
  if (nextMultiple && !wasMultiple) {
    if (current === '' || current == null || typeof current === 'boolean') return [];
    return [descriptor?.value_type === 'number' ? current : String(current)];
  }
  if (!nextMultiple && wasMultiple) return Array.isArray(current) ? (current[0] ?? '') : '';
  if (descriptor?.value_type !== 'number' && numericComparisonOperators.has(condition.operator)) {
    return current === '' || current == null ? '' : String(current);
  }
  return current;
};

export const fallbackConditionValueLabel = (id, sourceAction) => (
  sourceAction === 'merchandising.condition_values.search' ? String(id) : `#${id}`
);

export const filterIndexFields = (schema, query) => {
  const normalized = query.trim().toLocaleLowerCase();
  return Object.entries(schema)
    .filter(([, descriptor]) => descriptor.phases?.includes('index'))
    .map(([field, descriptor]) => ({ field, descriptor, label: humanField(field, descriptor) }))
    .filter(option => !normalized
      || option.label.toLocaleLowerCase().includes(normalized)
      || option.field.toLocaleLowerCase().includes(normalized))
    .sort((left, right) => left.label.localeCompare(right.label));
};

const flattenConditions = (group) => (group?.items || []).flatMap((item) => (
  Array.isArray(item.items) ? flattenConditions(item) : [item]
));

const conditionLabelKey = (condition) => `${condition?.field || ''}:${(
  Array.isArray(condition?.value) ? condition.value : [condition?.value]
).filter((value) => value !== '' && value != null).map(String).join(',')}`;

const conditionValueLabel = (condition, labels = {}) => {
  const values = (Array.isArray(condition?.value) ? condition.value : [condition?.value])
    .filter((value) => value !== '' && value != null);
  if (!values.length) return '';
  const rendered = values.map((value) => labels[String(value)]).filter(Boolean);
  if (!rendered.length) return '';
  return rendered.length > 2 ? `${rendered.slice(0, 2).join(', ')} +${rendered.length - 2}` : rendered.join(', ');
};

export const rankingRuleTitle = (rule, schema = {}, labelsByCondition = {}, fallback = '') => {
  const customName = String(rule?.name || '').trim();
  if (customName && customName.toLowerCase() !== 'product ranking rule') return customName;
  const conditions = flattenConditions(rule?.conditions);
  const first = conditions[0];
  if (!first) return fallback || String(rule?.kind || '');
  const field = humanField(first.field, descriptorForSavedCondition(first, schema));
  const value = conditionValueLabel(
    first,
    labelsByCondition[first.__id]
      || labelsByCondition[conditionLabelKey(first)]
      || labelsByCondition[first.field]
      || {},
  );
  const suffix = conditions.length > 1 ? ` +${conditions.length - 1}` : '';
  return `${field}${value ? ` · ${value}` : ''}${suffix}`;
};

const LogicToggle = ({ value, onChange }) => {
  const t = useLocalizedMessage();
  return (
    <div className={`mp-condition-connector ${value === 'any' ? 'is-any' : 'is-all'}`} role="group" aria-label={t('merchandising.workbench.logic.label')}>
      <button type="button" className={value === 'all' ? 'is-active' : ''} aria-pressed={value === 'all'} onClick={() => onChange('all')}>{t('merchandising.workbench.logic.and')}</button>
      <button type="button" className={value === 'any' ? 'is-active' : ''} aria-pressed={value === 'any'} onClick={() => onChange('any')}>{t('merchandising.workbench.logic.or')}</button>
    </div>
  );
};
LogicToggle.propTypes = { value: PropTypes.oneOf(['all', 'any']).isRequired, onChange: PropTypes.func.isRequired };

export const resolveRankingTitleLabels = async (dispatch, conditions, schema) => {
  const fields = [...new Set(conditions.map((condition) => condition.field))];
  const fieldResults = await runWithConcurrency(fields, async (field) => {
    const representative = conditions.find((condition) => condition.field === field);
    const descriptor = descriptorForSavedCondition(representative, schema);
    const selectorField = descriptor?.key || field;
    const action = descriptor?.value_source?.action;
    const fieldConditions = conditions.filter((condition) => condition.field === field);
    const ids = [...new Set(fieldConditions.flatMap((condition) => (
      Array.isArray(condition.value) ? condition.value : [condition.value]
    )).filter((value) => value !== '' && value != null).map(String))];
    if (!action || !ids.length) return [field, {}];
    if (action === 'product_selector.search') {
      const result = await dispatch(resolveProducts(ids));
      if (!resolveProducts.fulfilled.match(result)) return [field, {}];
      return [field, Object.fromEntries((result.payload.items || []).map((item) => [
        String(item.id), item.label || item.name || item.reference || `Product ${item.id}`,
      ]))];
    }
    const initial = await dispatch(searchMerchandisingValues({
      action, field: selectorField, query: '', limit: 50,
    }));
    if (!searchMerchandisingValues.fulfilled.match(initial)) return [field, {}];
    const labels = Object.fromEntries((initial.payload.items || []).map((item) => [
      String(item.id), item.label || item.name || item.breadcrumb,
    ]));
    const unresolved = ids.filter((id) => !labels[id]).slice(0, 10);
    const resolved = await runWithConcurrency(unresolved, id => dispatch(searchMerchandisingValues({
      action, field: selectorField, query: id, limit: 20,
    })), { concurrency: 2 });
    resolved.forEach((entry) => {
      if (entry.status !== 'succeeded') return;
      const result = entry.value;
      if (!searchMerchandisingValues.fulfilled.match(result)) return;
      (result.payload.items || []).forEach((item) => {
        labels[String(item.id)] = item.label || item.name || item.breadcrumb;
      });
    });
    return [field, labels];
  }, { concurrency: 4 });
  const entries = fieldResults
    .filter((entry) => entry.status === 'succeeded')
    .map((entry) => entry.value);
  return Object.fromEntries(entries);
};

const RankingLibrary = ({ basePath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const page = useSelector(selectActiveRulePage);
  const schema = useSelector((state) => state.merchandising.discovery.schema?.condition_fields || {});
  const request = useSelector((state) => state.merchandising.rules.request);
  const lifecycleById = useSelector((state) => state.merchandising.rules.lifecycleById);
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState([]);
  const [titleLabels, setTitleLabels] = useState({});
  const [deleteCandidates, setDeleteCandidates] = useState([]);

  const load = useCallback(() => dispatch(fetchMerchandisingRules({
    filters: {
      phase: 'index', kind: 'product_ranking', language_scope: 'neutral', limit: 100,
    },
  })), [dispatch]);
  useEffect(() => { load(); }, [load]);
  const firstTitleConditions = page.items
    .map((rule) => flattenConditions(rule.conditions)[0])
    .filter(Boolean);
  const titleLookupFingerprint = JSON.stringify(firstTitleConditions.map(conditionLabelKey));
  useEffect(() => {
    let current = true;
    if (!firstTitleConditions.length) return () => { current = false; };
    resolveRankingTitleLabels(dispatch, firstTitleConditions, schema).then((labels) => {
      if (current) setTitleLabels(labels);
    });
    return () => { current = false; };
  // The serialized first-condition set is the lookup identity; selector arrays are not stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, schema, titleLookupFingerprint]);
  const rows = page.items.filter((rule) => (
    rule.status !== 'archived'
    && (status === 'all' || rule.status === status)
    && `${rankingRuleTitle(rule, schema, titleLabels, C.PRODUCT_RANKING)} ${rule.description || ''}`.toLowerCase().includes(filter.toLowerCase())
  ));
  const allVisible = rows.length > 0 && rows.every((row) => selected.includes(row.id));
  const viewState = request.status === 'loading' && !page.items.length
    ? 'loading' : (request.status === 'error' ? 'error' : (rows.length ? 'ready' : 'empty'));
  const lifecycle = async (target) => {
    const eligible = selected.filter((id) => {
      const current = page.items.find((item) => item.id === id)?.status;
      if (target === 'active') return ['draft', 'suggested', 'disabled'].includes(current);
      if (target === 'disabled') return current === 'draft' || current === 'active';
      return false;
    });
    const results = await Promise.all(eligible.map((id) => {
      const rule = page.items.find((item) => item.id === id);
      if (!rule) return null;
      return dispatch(changeMerchandisingRuleStatus({
        id, revision: rule.revision, status: target, rule,
      }));
    }).filter(Boolean));
    if (results.every((result) => !result?.error)) {
      setSelected([]);
      load();
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
  };
  const selectedRules = selected
    .map((id) => page.items.find((item) => item.id === id))
    .filter(Boolean);
  const deleteCandidateIds = deleteCandidates.map((rule) => rule.id);
  const lifecycleError = [...new Set([...selected, ...deleteCandidateIds])]
    .map((id) => lifecycleById[id]?.error)
    .find(Boolean);
  const deleteBusy = deleteCandidateIds.some((id) => lifecycleById[id]?.status === 'loading');
  const canActivate = selectedRules.some((rule) => ['draft', 'suggested', 'disabled'].includes(rule.status));
  const canDisable = selectedRules.some((rule) => ['draft', 'active'].includes(rule.status));
  const canDelete = selectedRules.length > 0;
  const confirmDelete = async () => {
    if (deleteBusy) return;
    const results = await Promise.all(deleteCandidates.map((rule) => dispatch(
      deleteMerchandisingRule({ id: rule.id, revision: rule.revision }),
    )));
    const deletedIds = results.reduce((ids, result, index) => (
      deleteMerchandisingRule.fulfilled.match(result)
        ? [...ids, deleteCandidates[index].id]
        : ids
    ), []);
    if (deletedIds.length > 0) {
      setSelected((current) => current.filter((id) => !deletedIds.includes(id)));
      setDeleteCandidates((current) => current.filter((rule) => !deletedIds.includes(rule.id)));
      load();
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
    if (deletedIds.length === deleteCandidates.length) {
      setDeleteCandidates([]);
    }
  };

  return (
    <div>
      <PageHeader heading={C.PRODUCT_RANKING} body={C.PRODUCT_RANKING_DESCRIPTION} primaryLabel={C.CREATE_RANKING_RULE} onPrimary={() => history.push(`${basePath}/products/new`)} secondary={<><IndexStatusBar inline /><RuleTransferControls selectedRules={[]} onComplete={load} actions={['import']} /></>} />
      {(request.error || lifecycleError) && <div className="mp-inline-error" role="alert">{(request.error || lifecycleError).message}</div>}
      <StateBoundary state={viewState} onReady={load} copy={C} emptyActionLabel={C.CREATE_RANKING_RULE} onEmptyAction={() => history.push(`${basePath}/products/new`)}>
        <section className="mp-library-card" aria-label={C.PRODUCT_RANKING}>
          <div className="mp-library-toolbar">
            <SearchInput value={filter} onChange={setFilter} placeholder={C.RANKING_RULES_PLACEHOLDER} />
            <label className="mp-select-field"><span className="sr-only">{C.STATUS}</span><Select value={status} onChange={(event) => setStatus(event.target.value)} variant="outlined"><MenuItem value="all">{C.ALL_STATUSES}</MenuItem><MenuItem value="active">{C.ACTIVE}</MenuItem><MenuItem value="draft">{C.DRAFT}</MenuItem><MenuItem value="disabled">{C.DISABLED}</MenuItem></Select></label>
            <RuleTransferControls selectedRules={selectedRules} actions={['export']} />
          </div>
          <div className="mp-table-scroll"><div className="mp-rule-table mp-rule-table--ranking" role="table">
            <div className="mp-rule-row mp-rule-row--head" role="row"><span role="columnheader"><SelectionCheckbox checked={allVisible} label={C.SELECT_ALL} onChange={() => setSelected(allVisible ? [] : rows.map((row) => row.id))} /></span><span role="columnheader">{C.APPLIES_TO}</span><span role="columnheader">{C.EFFECT}</span><span role="columnheader">{C.STATUS}</span><span role="columnheader">{C.UPDATED}</span><span role="columnheader" /></div>
            {rows.map((rule) => {
              const title = rankingRuleTitle(rule, schema, titleLabels, C.PRODUCT_RANKING);
              return (
              <div className="mp-rule-row" role="row" key={rule.id}>
                <span role="cell"><SelectionCheckbox checked={selected.includes(rule.id)} label={t('merchandising.rule.select', { name: title })} onChange={() => setSelected((current) => current.includes(rule.id) ? current.filter((id) => id !== rule.id) : [...current, rule.id])} /></span>
                <span role="cell" className="mp-rule-primary"><Link to={`${basePath}/products/${rule.id}`}>{title}</Link></span>
                <span role="cell">
                  {rule.actions?.some((action) => action.type === 'score.multiply') && (
                    <PriorityLabel factor={Number(rule.actions.find((action) => action.type === 'score.multiply').value)} copy={C} />
                  )}
                  {rule.actions?.filter((action) => action.type === 'search_terms.add').map((action) => (
                    <span className="mp-ranking-rule-effect" key={action.priority}>
                      {t('merchandising.workbench.searchTerms')} · {t(`merchandising.workbench.searchTerms.${action.priority}`)}
                    </span>
                  ))}
                </span>
                <span role="cell"><StatusChip status={rule.status} copy={C} /></span>
                <span role="cell" className="mp-muted">{formatLocalDateTime(rule.updated_at, intl.locale) || '—'}</span>
                <span role="cell" className="mp-row-actions"><IconButton aria-label={t('merchandising.workbench.duplicateNamed', { name: title })} onClick={() => history.push(`${basePath}/products/new?from=${rule.id}`)}><FileCopyOutlinedIcon fontSize="small" /></IconButton><IconButton aria-label={t('merchandising.workbench.deleteNamed', { name: title })} onClick={() => setDeleteCandidates([rule])}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
              </div>
              );
            })}
          </div></div>
        </section>
        {selected.length > 0 && <div className="mp-bulk-bar"><strong>{selected.length} {C.SELECTED}</strong><RuleTransferControls selectedRules={selectedRules} onComplete={load} actions={['duplicate']} />{canActivate && <Button onClick={() => lifecycle('active')}>{t('merchandising.action.activate')}</Button>}{canDisable && <Button onClick={() => lifecycle('disabled')}>{C.DISABLE}</Button>}{canDelete && <Button className="mp-destructive-action" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteCandidates(selectedRules)}>{t('merchandising.action.delete')}</Button>}</div>}
      </StateBoundary>
      <Dialog className="mp-dialog" open={deleteCandidates.length > 0} onClose={() => { if (!deleteBusy) setDeleteCandidates([]); }} maxWidth="xs" fullWidth>
        <DialogTitle>{t('merchandising.workbench.deleteTitle')}</DialogTitle>
        <DialogContent>
          <p>{t('merchandising.workbench.deleteWarning', { count: deleteCandidates.length })}</p>
          {lifecycleError && <div className="mp-inline-error" role="alert">{lifecycleError.message}</div>}
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="outlined" disabled={deleteBusy} onClick={() => setDeleteCandidates([])}>{C.CANCEL}</Button><Button className="mp-danger-button" variant="contained" disabled={deleteBusy} startIcon={deleteBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />} onClick={confirmDelete}>{deleteBusy ? t('merchandising.action.deleting') : t('merchandising.action.delete')}</Button></DialogActions>
      </Dialog>
    </div>
  );
};
RankingLibrary.propTypes = { basePath: PropTypes.string.isRequired };

const ProductConditionPicker = ({ multiple, onChange, productApi, selectedIds, onItems }) => {
  const t = useLocalizedMessage();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let current = true;
    if (!query.trim()) {
      setItems([]);
      setLoading(false);
      return () => { current = false; };
    }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await productApi.searchProducts(query.trim(), 20);
        if (!current) return;
        const next = Array.isArray(result) ? result : (result?.items || []);
        setItems(next);
        onItems(next);
      } catch (error) {
        if (current && error) setItems([]);
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => { current = false; window.clearTimeout(timer); };
  }, [onItems, productApi, query]);

  const toggle = (id) => {
    const normalized = String(id);
    if (selectedIds.includes(normalized)) {
      onChange(multiple ? selectedIds.filter((entry) => entry !== normalized) : '');
      return;
    }
    onChange(multiple ? [...selectedIds, normalized] : normalized);
  };

  return (
    <div className="mp-workbench-value-selector">
      <SearchInput value={query} onChange={setQuery} placeholder={t('product.selector.search.placeholder')} />
      <div className="mp-value-picker" role="group" aria-label={t('merchandising.selector.results')} aria-busy={loading}>
        {items.map((product) => {
          const id = String(product.id);
          const selected = selectedIds.includes(id);
          return (
            <label key={id} className={`mp-value-option mp-value-option--product ${selected ? 'is-selected' : ''}`}>
              <SelectionCheckbox checked={selected} label={product.name || `#${id}`} onChange={() => toggle(id)} />
              <ProductImage product={product} size="thumb" />
              <span className="mp-value-option-copy">
                <strong>{product.name || `#${id}`}</strong>
                <small>{product.reference || product.vendorCode || `#${id}`}</small>
              </span>
            </label>
          );
        })}
        {!loading && items.length === 0 && <div className="mp-value-picker-empty">{t('merchandising.selector.empty')}</div>}
      </div>
    </div>
  );
};
ProductConditionPicker.propTypes = {
  multiple: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  productApi: PropTypes.object.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onItems: PropTypes.func.isRequired,
};

export const ConditionValue = ({ condition, descriptor, onChange, onItems, productApi }) => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState({});
  const multiple = multipleOperators.has(condition.operator);
  const textContains = ['text_contains_any', 'text_not_contains_any',
    'text_contains_any_case'].includes(condition.operator);
  const selectorField = descriptor?.key || condition.field;
  const sourceAction = textContains ? null : descriptor?.value_source?.action;
  const values = multiple ? (Array.isArray(condition.value) ? condition.value : []) : condition.value;
  const selectedIds = (Array.isArray(values) ? values : [values]).filter((value) => value !== '');
  const rememberItems = useCallback((items) => {
    setLabels((current) => ({
      ...current,
      ...Object.fromEntries(items.map((item) => [
        String(item.id),
        item.label || item.name || item.breadcrumb
          || fallbackConditionValueLabel(item.id, sourceAction),
      ])),
    }));
    onItems(items);
  }, [onItems, sourceAction]);
  const removeValue = (id) => onChange(multiple ? selectedIds.filter((value) => value !== id) : '');
  const showAsChip = sourceAction || descriptor?.value_type === 'boolean';
  const numeric = isNumericCondition(condition, descriptor);
  const compactLiteralList = !showAsChip && Array.isArray(condition.value)
    && condition.operator !== 'between';
  const compactValues = textContains || compactLiteralList;
  const selectedFingerprint = selectedIds.map(String).join('|');

  useEffect(() => {
    const missing = selectedIds.map(String).filter((id) => !labels[id]);
    if (!sourceAction || !missing.length) return undefined;
    let current = true;
    const resolve = async () => {
      if (sourceAction === 'product_selector.search') {
        const response = await productApi.resolveProducts(missing);
        if (current) rememberItems(Array.isArray(response) ? response : (response?.items || []));
        return;
      }
      const responses = await Promise.all(missing.map((id) => dispatch(searchMerchandisingValues({
        action: sourceAction,
        field: selectorField,
        query: id,
        limit: 20,
      }))));
      if (!current) return;
      const items = responses.flatMap((result) => (
        searchMerchandisingValues.fulfilled.match(result) ? (result.payload?.items || []) : []
      ));
      rememberItems(items.filter((item) => missing.includes(String(item.id))));
    };
    resolve().catch(() => {});
    return () => { current = false; };
  // Labels are resolved only when the selected identity set changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productApi, selectedFingerprint, selectorField, sourceAction]);

  if (condition.operator === 'exists') return null;

  if (regexOperators.has(condition.operator)) {
    const error = merchandisingRegexError(condition.value);
    const errorMessage = error ? t(`merchandising.condition.regex.${error}`) : null;
    return (
      <label className={`mp-condition-number-single mp-condition-regex ${error ? 'is-invalid' : ''}`}>
        <input
          aria-invalid={Boolean(error)}
          aria-label={t('merchandising.condition.regex.ariaLabel', {
            field: humanField(condition.field, descriptor),
          })}
          type="text"
          maxLength={200}
          spellCheck={false}
          placeholder={t('merchandising.condition.regex.placeholder')}
          value={condition.value}
          onChange={(event) => onChange(event.target.value)}
        />
        {errorMessage && <span className="mp-condition-regex-error" role="alert">{errorMessage}</span>}
      </label>
    );
  }

  if (numeric) {
    const range = condition.operator === 'between'
      ? (Array.isArray(condition.value) ? condition.value : ['', ''])
      : null;
    const numericValue = (value) => (value === '' ? '' : Number(value));
    const rangeInvalid = range
      && range.every((value) => value !== '' && Number.isFinite(Number(value)))
      && Number(range[0]) > Number(range[1]);
    return condition.operator === 'between' ? (
      <div className={`mp-condition-number-range ${rangeInvalid ? 'is-invalid' : ''}`}>
        <label><span>{t('merchandising.condition.from')}</span><input aria-invalid={rangeInvalid} aria-label={`${humanField(condition.field, descriptor)} ${t('merchandising.condition.from')}`} type="number" inputMode="decimal" value={range[0]} onChange={(event) => onChange([numericValue(event.target.value), range[1]])} /></label>
        <span className="mp-condition-range-separator" aria-hidden="true">—</span>
        <label><span>{t('merchandising.condition.to')}</span><input aria-invalid={rangeInvalid} aria-label={`${humanField(condition.field, descriptor)} ${t('merchandising.condition.to')}`} type="number" inputMode="decimal" value={range[1]} onChange={(event) => onChange([range[0], numericValue(event.target.value)])} /></label>
        {rangeInvalid && <span className="mp-condition-range-error" role="alert">{t('merchandising.condition.rangeOrder')}</span>}
      </div>
    ) : (
      <label className="mp-condition-number-single"><input aria-label={`${humanField(condition.field, descriptor)} ${t('merchandising.condition.value')}`} type="number" inputMode="decimal" value={condition.value} onChange={(event) => onChange(numericValue(event.target.value))} /></label>
    );
  }

  return (
    <div className="mp-condition-values">
      {showAsChip && selectedIds.map((id) => (
        <Chip key={id} label={labels[String(id)] || (descriptor?.value_type === 'boolean'
          ? t(`merchandising.value.${String(id)}`)
          : fallbackConditionValueLabel(id, sourceAction))} onDelete={() => removeValue(id)} />
      ))}
      {!showAsChip && condition.operator === 'between' && Array.isArray(condition.value) && (
        <Chip label={`${condition.value[0]} ${t('merchandising.workbench.rangeTo')} ${condition.value[1]}`} />
      )}
      {compactValues && selectedIds.slice(0, 3).map((value) => (
        <Chip key={String(value)} label={String(value)} onDelete={() => removeValue(value)} />
      ))}
      {compactValues && selectedIds.length > 3 && (
        <Chip
          label={`+${selectedIds.length - 3}`}
          onClick={() => setOpen(true)}
          aria-label={`${t('merchandising.workbench.chooseValues')} · ${selectedIds.length}`}
        />
      )}
      {!showAsChip && !compactValues && condition.operator !== 'between' && condition.value !== '' && (
        <Chip label={String(condition.value)} />
      )}
      <Button
        aria-label={`${t('merchandising.workbench.addValue')} ${humanField(condition.field, descriptor)}`}
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
      >
        {t('merchandising.workbench.addValue')}
      </Button>
      <Dialog className="mp-dialog" open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle className="mp-dialog-title-row"><span>{t('merchandising.workbench.chooseValues')} · {humanField(condition.field, descriptor)}</span><IconButton aria-label={C.CLOSE} onClick={() => setOpen(false)}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent>
          {sourceAction === 'product_selector.search' && (
            <ProductConditionPicker multiple={multiple} selectedIds={selectedIds.map(String)} onChange={onChange} productApi={productApi} onItems={rememberItems} />
          )}
          {sourceAction && sourceAction !== 'product_selector.search' && (
            <SchemaValueSelector action={sourceAction} field={selectorField} value={values} multiple={multiple} onChange={onChange} onItems={rememberItems} presentation="workbench" />
          )}
          {!sourceAction && descriptor?.value_type === 'boolean' && (
            <div className="mp-value-picker">
              {[true, false].map((value) => {
                const selected = Boolean(condition.value) === value;
                const label = t(value ? 'merchandising.value.true' : 'merchandising.value.false');
                return <label key={String(value)} className={`mp-value-option ${selected ? 'is-selected' : ''}`}><SelectionCheckbox checked={selected} label={label} onChange={() => onChange(value)} /><span className="mp-value-option-copy"><strong>{label}</strong></span></label>;
              })}
            </div>
          )}
          {!sourceAction && descriptor?.value_type !== 'boolean' && descriptor?.value_type !== 'number' && <label className="mp-dialog-value-field"><span>{t('merchandising.condition.value')}</span><input value={Array.isArray(values) ? values.join(', ') : values} onChange={(event) => onChange(multiple ? event.target.value.split(',').map((value) => value.trim()).filter(Boolean) : event.target.value)} /></label>}
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="contained" color="primary" onClick={() => setOpen(false)}>{C.DONE}</Button></DialogActions>
      </Dialog>
    </div>
  );
};
ConditionValue.propTypes = {
  condition: PropTypes.object.isRequired,
  descriptor: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onItems: PropTypes.func,
  productApi: PropTypes.object.isRequired,
};
ConditionValue.defaultProps = { descriptor: null, onItems: () => {} };

export const OperatorPicker = ({ label, onChange, options, value }) => {
  const t = useLocalizedMessage();
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef({});
  const [pickerId] = useState(() => `mp-operator-picker-${uuid()}`);
  const groups = groupConditionOperatorOptions(options);
  const flatOptions = groups.flatMap(group => group.options);
  const selected = options.find(option => option.value === value) || options[0];
  const open = Boolean(anchorEl);

  const close = (restoreFocus = false) => {
    setAnchorEl(null);
    if (restoreFocus) window.setTimeout(() => {
      if (triggerRef.current) triggerRef.current.focus();
    }, 0);
  };
  const focusSelected = () => {
    const target = optionRefs.current[value] || optionRefs.current[flatOptions[0]?.value];
    if (target) target.focus();
  };
  const openPicker = (event) => setAnchorEl(event.currentTarget);
  const select = (selection) => {
    if (selection !== value) onChange(selection);
    close(true);
  };
  const handleTriggerKeyDown = (event) => {
    if (!['ArrowDown', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handlePickerKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = flatOptions.findIndex(option => (
      optionRefs.current[option.value] === document.activeElement
    ));
    let nextIndex = 0;
    if (event.key === 'End') nextIndex = flatOptions.length - 1;
    else if (event.key === 'ArrowUp') {
      nextIndex = currentIndex <= 0 ? flatOptions.length - 1 : currentIndex - 1;
    } else if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < 0 || currentIndex === flatOptions.length - 1
        ? 0 : currentIndex + 1;
    }
    const nextOption = flatOptions[nextIndex];
    const nextTarget = nextOption && optionRefs.current[nextOption.value];
    if (nextTarget) nextTarget.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`mp-condition-operator mp-condition-operator-trigger ${open ? 'is-open' : ''}`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? pickerId : undefined}
        title={selected?.label}
        onClick={openPicker}
        onKeyDown={handleTriggerKeyDown}
      >
        <span>{selected?.label}</span>
        <ArrowDropDownIcon aria-hidden="true" />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => close(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: `mp-operator-picker-paper has-${Math.min(groups.length, 4)}-columns`,
        }}
        TransitionProps={{ onEntered: focusSelected }}
      >
        <div
          id={pickerId}
          className="mp-operator-picker"
          role="listbox"
          aria-label={label}
          onKeyDown={handlePickerKeyDown}
        >
          {groups.map(({ group, options: groupOptions }) => {
            const headingId = `${pickerId}-${group}`;
            return (
              <section key={group} className="mp-operator-group" role="group" aria-labelledby={headingId}>
                <strong id={headingId}>{t(`merchandising.condition.operatorGroup.${group}`)}</strong>
                <div>
                  {groupOptions.map(option => (
                    <button
                      key={option.value}
                      ref={(node) => { optionRefs.current[option.value] = node; }}
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      className={option.value === value ? 'is-selected' : ''}
                      onClick={() => select(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </Popover>
    </>
  );
};
OperatorPicker.propTypes = {
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    group: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.string.isRequired,
};

export const ConditionRow = ({
  condition, schema, productApi, dragging, dropTarget, onDragStart, onDragEnd,
  onDragEnter, onDrop, onItems, onRemove, onUpdate, advancedConditions,
}) => {
  const t = useLocalizedMessage();
  const descriptor = descriptorForSavedCondition(condition, schema, advancedConditions);
  const operatorOptions = conditionOperatorOptions(
    descriptor, condition.operator, t, advancedConditions,
  );
  const selectedOperator = condition.operator === 'exists'
    ? `exists:${condition.value !== false}` : condition.operator;
  const selectOperator = (selection) => {
    const operator = selection.startsWith('exists:') ? 'exists' : selection;
    onUpdate({
      operator,
      value: conditionValueForOperatorChange(condition, descriptor, selection),
    });
  };
  return (
    <div
      className={`mp-condition-row ${isNumericCondition(condition, descriptor) ? 'is-numeric' : ''} ${condition.operator === 'exists' ? 'is-unary' : ''} ${dragging ? 'is-dragging' : ''} ${dropTarget ? 'is-group-target' : ''}`}
      draggable
      onDragStart={(event) => onDragStart(event, condition.__id)}
      onDragOver={(event) => event.preventDefault()}
      onDragEnter={() => onDragEnter(condition.__id)}
      onDragEnd={onDragEnd}
      onDrop={(event) => onDrop(event, condition.__id)}
    >
      <span className="mp-condition-drag" aria-hidden="true"><DragIndicatorIcon /></span>
      <div className="mp-condition-content">
        <strong className="mp-condition-title">{humanField(condition.field, descriptor)}</strong>
        <div className="mp-condition-controls">
          {operatorOptions.length > 1 ? (
            <OperatorPicker
              value={selectedOperator}
              label={t('merchandising.condition.operator')}
              options={operatorOptions}
              onChange={selectOperator}
            />
          ) : (
            <span className="mp-condition-operator-static">
              {operatorOptions[0]?.label || humanOperator(condition.operator)}
            </span>
          )}
          <ConditionValue condition={condition} descriptor={descriptor} productApi={productApi} onItems={onItems} onChange={(value) => onUpdate({ value })} />
        </div>
      </div>
      <IconButton aria-label={t('merchandising.workbench.removeFilter', { name: humanField(condition.field, descriptor) })} onClick={onRemove}><DeleteOutlineIcon /></IconButton>
      {dropTarget && <span className="mp-condition-drop-hint">{t('merchandising.workbench.groupFilterHint')}</span>}
    </div>
  );
};
ConditionRow.propTypes = {
  condition: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  productApi: PropTypes.object.isRequired,
  dragging: PropTypes.bool.isRequired,
  dropTarget: PropTypes.bool.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onItems: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  advancedConditions: PropTypes.bool,
};
ConditionRow.defaultProps = { advancedConditions: true };

const RankingEditor = ({ basePath, ruleId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const { productProfile, capabilities = {} } = useShellAppContext();
  const isCloud = productProfile?.id === 'cloud';
  const searchPhrasesAvailable = capabilities.merchandising_search_terms_v1 === true;
  const advancedConditions = capabilities.merchandising_advanced_conditions_v1 === true;
  const schema = useSelector((state) => state.merchandising.discovery.schema?.condition_fields || {});
  const entities = useSelector((state) => state.merchandising.rules.entities);
  const editorRequest = useSelector((state) => state.merchandising.editor.request);
  const previewState = useSelector((state) => state.merchandising.previews.index);
  const [model, setModel] = useState(emptyRankingWorkbench);
  const [baseline, setBaseline] = useState(emptyRankingWorkbench);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [previewQueued, setPreviewQueued] = useState(false);
  const [dragged, setDragged] = useState(null);
  const [target, setTarget] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [resolvedProducts, setResolvedProducts] = useState({});
  const [conditionLabels, setConditionLabels] = useState({});
  const [localError, setLocalError] = useState(null);
  const [priorityPosition, setPriorityPosition] = useState(
    () => rankingFactorToPosition(emptyRankingWorkbench().factor),
  );
  const loaded = useRef(null);
  const previewGeneration = useRef(0);
  const duplicateId = new URLSearchParams(location.search).get('from');
  const sourceId = ruleId || duplicateId;
  const filterOptions = useMemo(() => filterIndexFields(schema, filterQuery)
    .map(option => ({
      ...option,
      descriptor: descriptorForCapabilities(option.descriptor, advancedConditions),
    }))
    .filter(option => option.descriptor.operators.length > 0), [
    advancedConditions, filterQuery, schema,
  ]);
  const openFilterDialog = () => {
    setFilterQuery('');
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    setLocalError(null);
    setPreviewPage(1);
    setConditionLabels({});
    setFilterDialogOpen(false);
    setScheduleEnabled(false);
    setPreviewQueued(false);
    dispatch(clearRuleEditorFeedback());
    dispatch(clearPreview('index'));
    loaded.current = null;
    if (!sourceId) {
      const empty = emptyRankingWorkbench();
      setModel(empty);
      setBaseline(empty);
      return;
    }
    if (!entities[sourceId]) dispatch(fetchMerchandisingRule({ id: sourceId }));
  // A route identity change always starts a fresh editor session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sourceId]);
  useEffect(() => {
    const rule = sourceId ? entities[sourceId] : null;
    if (!rule || loaded.current === sourceId) return;
    const next = rankingRuleToWorkbench(rule);
    if (!ruleId) {
      next.id = null;
      next.revision = null;
      next.status = 'draft';
      next.name = '';
    }
    setModel(next);
    setBaseline(next);
    setScheduleEnabled(Boolean(next.validFrom || next.validTo));
    loaded.current = sourceId;
  }, [entities, ruleId, sourceId]);
  useEffect(() => {
    setPriorityPosition(rankingFactorToPosition(model.factor));
  }, [model.factor]);

  const titleCondition = flattenConditions(model.conditions)[0] || null;
  const titleConditionFingerprint = titleCondition ? conditionLabelKey(titleCondition) : '';
  useEffect(() => {
    let current = true;
    if (!titleCondition) return () => { current = false; };
    resolveRankingTitleLabels(dispatch, [titleCondition], schema).then((labels) => {
      if (current) setConditionLabels((existing) => ({ ...existing, ...labels }));
    });
    return () => { current = false; };
  // The serialized field/value pair is the label lookup identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, schema, titleConditionFingerprint]);

  const unavailableSearchTerms = !searchPhrasesAvailable
    && (Boolean(model.boostedTermsText?.trim()) || Boolean(model.midTermsText?.trim()));
  const unavailableAdvancedConditions = !advancedConditions
    && ruleUsesAdvancedConditions({ conditions: model.conditions }, schema);
  const valid = !unavailableSearchTerms && !unavailableAdvancedConditions
    && rankingExpressionValid(model.conditions)
    && Number.isFinite(model.factor) && model.factor >= 0.1 && model.factor <= 5
    && (!searchPhrasesAvailable || (rankingSearchTermsValid(model.boostedTermsText)
      && rankingSearchTermsValid(model.midTermsText)))
    && (model.factor !== 1 || (searchPhrasesAvailable
      && (Boolean(model.boostedTermsText?.trim()) || Boolean(model.midTermsText?.trim()))));
  const generatedName = rankingRuleTitle(
    { name: '', conditions: model.conditions }, schema, conditionLabels, C.PRODUCT_RANKING,
  );
  const displayName = model.name.trim() || generatedName || C.PRODUCT_RANKING;
  // Old imports used translated facet labels as storage keys. Keep reading those
  // rules, but preview and persist the schema's stable key so every editor and
  // indexing client sees one canonical rule shape.
  const canonicalConditions = useMemo(
    () => canonicalizeSavedConditionFields(model.conditions, schema),
    [model.conditions, schema],
  );
  const draft = useMemo(
    () => rankingWorkbenchRule({ ...model, conditions: canonicalConditions }),
    [canonicalConditions, model],
  );
  const fingerprint = JSON.stringify(draft);
  useEffect(() => {
    const ownGeneration = ++previewGeneration.current;
    if (!valid) {
      setPreviewQueued(false);
      dispatch(clearPreview('index'));
      return undefined;
    }
    setPreviewQueued(true);
    const timer = window.setTimeout(async () => {
      await dispatch(runMerchandisingPreview({
        workspace: 'index',
        payload: { draft_rule: draft, sample_limit: 50 },
      }));
      if (previewGeneration.current === ownGeneration) setPreviewQueued(false);
    }, 650);
    return () => {
      window.clearTimeout(timer);
      if (previewGeneration.current === ownGeneration) previewGeneration.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fingerprint, valid]);
  const samples = useMemo(() => previewState.result?.samples || [], [previewState.result]);
  const sampleIds = useMemo(
    () => samples.map((sample) => String(sample.product_id)),
    [samples],
  );
  const previewBusy = previewQueued
    || previewState.request.refreshing
    || previewState.request.status === 'loading';
  useEffect(() => {
    if (!sampleIds.length) { setResolvedProducts({}); return; }
    dispatch(resolveProducts(sampleIds)).then((result) => {
      if (!resolveProducts.fulfilled.match(result)) return;
      setResolvedProducts(Object.fromEntries((result.payload.items || []).map((product) => [String(product.id), product])));
    });
  }, [dispatch, sampleIds]);

  const productApi = useMemo(() => ({
    searchProducts: async (query, limit) => {
      const result = await dispatch(searchProducts({ query, limit }));
      if (searchProducts.fulfilled.match(result)) return result.payload;
      throw new Error(result.payload || 'Product search failed.');
    },
    resolveProducts: async (ids) => {
      const result = await dispatch(resolveProducts(ids));
      if (resolveProducts.fulfilled.match(result)) return result.payload;
      throw new Error(result.payload || 'Product resolve failed.');
    },
  }), [dispatch]);
  const dirty = JSON.stringify(model) !== JSON.stringify(baseline);
  const unsavedGuard = useUnsavedChangesGuard(dirty);
  const readOnly = model.status === 'archived';
  const active = model.status === 'active';
  const scheduleIsValid = isCloud || validSchedule(model.validFrom, model.validTo);
  const updateCondition = (id, update) => setModel((current) => ({ ...current, conditions: updateRankingCondition(current.conditions, id, update) }));
  const save = async (publish) => {
    if (!valid) { setLocalError(t('merchandising.workbench.rankingIncomplete')); return; }
    if (!scheduleIsValid) { setLocalError(t('merchandising.workbench.invalidSchedule')); return; }
    setLocalError(null);
    const savedAction = await dispatch(saveMerchandisingRule({
      ...(model.id ? { id: model.id, revision: model.revision } : { clientMutationId: uuid() }),
      rule: draft,
    }));
    if (!saveMerchandisingRule.fulfilled.match(savedAction)) return;
    let saved = savedAction.payload.rule || savedAction.payload;
    if (publish && saved.status !== 'active') {
      const activated = await dispatch(changeMerchandisingRuleStatus({
        id: saved.id, revision: saved.revision, status: 'active', rule: saved,
      }));
      if (!changeMerchandisingRuleStatus.fulfilled.match(activated)) return;
      saved = activated.payload.rule || activated.payload;
    }
    const canonical = rankingRuleToWorkbench(saved);
    setModel(canonical);
    setBaseline(canonical);
    loaded.current = String(saved.id);
    unsavedGuard.navigateWithoutPrompt(() => history.replace(`${basePath}/products/${saved.id}`));
  };

  const renderGroup = (group, depth = 0) => (
    <div className={depth ? 'mp-condition-group' : 'mp-condition-list'} key={group.__id}>
      {depth > 0 && <div className="mp-condition-group-header"><strong>{t('merchandising.workbench.filterGroup')}</strong><Button size="small" onClick={() => setModel((current) => ({ ...current, conditions: ungroupRankingConditions(current.conditions, group.__id) }))}>{t('merchandising.workbench.ungroup')}</Button></div>}
      {group.items.map((item, index) => (
        <React.Fragment key={item.__id}>
          {index > 0 && <LogicToggle value={group.match} onChange={(match) => setModel((current) => ({ ...current, conditions: updateRankingCondition(current.conditions, group.__id, { match }) }))} />}
          {Array.isArray(item.items) ? renderGroup(item, depth + 1) : (
            <ConditionRow
              condition={item}
              schema={schema}
              advancedConditions={advancedConditions}
              productApi={productApi}
              dragging={dragged === item.__id}
              dropTarget={target === item.__id && dragged !== item.__id}
              onDragStart={(event, id) => { event.dataTransfer.setData('text/plain', id); setDragged(id); }}
              onDragEnter={(id) => { if (dragged && dragged !== id && depth < 2) setTarget(id); }}
              onDragEnd={() => { setDragged(null); setTarget(null); }}
              onDrop={(event, targetId) => { event.preventDefault(); const source = event.dataTransfer.getData('text/plain'); setModel((current) => ({ ...current, conditions: groupRankingConditions(current.conditions, source, targetId) })); setDragged(null); setTarget(null); }}
              onItems={(items) => setConditionLabels((currentLabels) => ({
                ...currentLabels,
                [item.__id]: {
                  ...(currentLabels[item.__id] || {}),
                  ...Object.fromEntries(items.map((entry) => [
                    String(entry.id),
                    entry.label || entry.name || entry.breadcrumb || String(entry.id),
                  ])),
                },
              }))}
              onRemove={() => setModel((current) => ({ ...current, conditions: removeRankingCondition(current.conditions, item.__id) }))}
              onUpdate={(update) => updateCondition(item.__id, update)}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
  const visibleSamples = samples.slice((previewPage - 1) * 10, previewPage * 10);
  const pages = Math.max(1, Math.ceil(samples.length / 10));
  useEffect(() => {
    if (previewPage > pages) setPreviewPage(pages);
  }, [pages, previewPage]);

  return (
    <div>
      <PageHeader
        eyebrow={<Link className="mp-back-link" to={`${basePath}/products`}><ArrowBackIcon />{C.BACK_TO_RANKING}</Link>}
        heading={model.id ? displayName : C.CREATE_RANKING_RULE}
        body={null}
        primaryLabel={active ? t('merchandising.action.saveChanges') : C.PUBLISH}
        primaryIcon={active ? <SaveIcon /> : <PublishIcon />}
        primaryDisabled={readOnly || !valid || !scheduleIsValid || (active && !dirty)}
        onPrimary={() => save(!active)}
        secondary={<>{dirty && <Button variant="outlined" onClick={() => { setModel(baseline); setScheduleEnabled(Boolean(baseline.validFrom || baseline.validTo)); }}>{C.RESET_CHANGES}</Button>}{!active && dirty && <Button variant="outlined" disabled={readOnly || !valid || !scheduleIsValid} onClick={() => save(false)}>{model.status === 'draft' ? t('merchandising.action.saveDraft') : t('merchandising.action.saveChanges')}</Button>}<IndexStatusBar inline /></>}
      />
      {readOnly && <div className="mp-inline-error" role="status">{t('merchandising.workbench.archivedRanking')}</div>}
      {(localError || editorRequest.error || previewState.request.error) && (
        <InlineNotice
          dismissLabel={C.CLOSE}
          onDismiss={() => {
            setLocalError(null);
            dispatch(clearRuleEditorFeedback());
            if (previewState.request.error) dispatch(clearPreview('index'));
          }}
        >
          {localError || editorRequest.error?.message || previewState.request.error?.message}
        </InlineNotice>
      )}
      {(unavailableSearchTerms || unavailableAdvancedConditions) && (
        <InlineNotice>{t('merchandising.capability.unavailable')}</InlineNotice>
      )}
      <div className="mp-ranking-workbench">
        <aside className="mp-definition-panel">
          <section className="mp-rule-identity">
            <label className="mp-plain-field">
              <span>{t('merchandising.workbench.ruleNameOptional')}</span>
              <input
                type="text"
                maxLength={160}
                disabled={readOnly}
                value={model.name}
                placeholder={generatedName || t('merchandising.workbench.generatedRuleName')}
                onChange={(event) => setModel({ ...model, name: event.target.value })}
              />
            </label>
          </section>
          <section>
            <div className="mp-section-heading"><h2>{t('merchandising.workbench.productFilters')}</h2></div>
            {model.conditions.items.length ? renderGroup(model.conditions) : <button type="button" className="mp-condition-empty" onClick={openFilterDialog}><span aria-hidden="true"><AddIcon /></span><strong>{t('merchandising.workbench.addFilter')}</strong></button>}
            {model.conditions.items.length > 0 && <div className="mp-inline-actions"><Tooltip title={t('merchandising.workbench.addFilter')}><IconButton className="mp-add-filter-button" aria-label={t('merchandising.workbench.addFilter')} onClick={openFilterDialog}><AddIcon /></IconButton></Tooltip></div>}
          </section>
          <section>
            <div className="mp-section-heading"><h2>{t('merchandising.workbench.productPriority')}</h2><PriorityLabel factor={model.factor} copy={C} /></div>
            <Slider
              className="mp-bipolar-slider"
              min={0}
              max={100}
              step={0.1}
              marks={rankingFactorMarks}
              disabled={readOnly}
              value={priorityPosition}
              onChange={(event, value) => setPriorityPosition(value)}
              onChangeCommitted={(event, value) => {
                const factor = rankingPositionToFactor(value);
                setPriorityPosition(rankingFactorToPosition(factor));
                setModel(current => (current.factor === factor ? current : { ...current, factor }));
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={value => `${rankingPositionToFactor(value)}×`}
              getAriaValueText={value => `${rankingPositionToFactor(value)}×`}
              aria-label={t('merchandising.workbench.productPriority')}
            />
            <div className="mp-scale-labels" aria-hidden="true">{[
              { factor: 0.25, label: t('merchandising.workbench.priority.extraLow') },
              { factor: 0.5, label: t('merchandising.workbench.priority.lower') },
              { factor: 1, label: t('merchandising.workbench.priority.natural') },
              { factor: 2, label: t('merchandising.workbench.priority.higher') },
              { factor: 4, label: t('merchandising.workbench.priority.extraHigh') },
            ].map(({ factor, label }) => <span key={factor} style={{ '--mp-scale-position': `${rankingFactorToPosition(factor)}%` }}><strong>{label}</strong><small>{factor}{t('merchandising.workbench.multiplierSymbol')}</small></span>)}</div>
          </section>
          {searchPhrasesAvailable && <SearchPhraseEditor
            key={sourceId || 'new'}
            boostedTermsText={model.boostedTermsText}
            midTermsText={model.midTermsText}
            disabled={readOnly}
            onChange={values => setModel(current => ({ ...current, ...values }))}
          />}
          {!isCloud && (
            <ScheduleCard
              enabled={scheduleEnabled}
              validFrom={model.validFrom}
              validTo={model.validTo}
              disabled={readOnly}
              copy={C}
              onEnabledChange={(enabled) => {
                setScheduleEnabled(enabled);
                if (!enabled) setModel((currentModel) => ({
                  ...currentModel, validFrom: null, validTo: null,
                }));
              }}
              onChange={(field, value) => setModel((currentModel) => ({
                ...currentModel, [field]: value,
              }))}
            />
          )}
        </aside>
        <main className="mp-ranking-preview">
          <div className="mp-ranking-preview-header"><h2>{t('merchandising.preview.title')}</h2><span className="mp-result-count"><strong>{previewState.result?.count_kind === 'bounded_lower_bound' ? '≥ ' : ''}{previewState.result?.matched_count || 0}</strong> {t('merchandising.workbench.matching')}</span></div>
          <div className="mp-ranking-product-list" aria-busy={previewBusy}>
            {visibleSamples.map((sample, index) => {
              const product = resolvedProducts[String(sample.product_id)] || { id: sample.product_id, name: `Product #${sample.product_id}`, thumbnail: '' };
              const searchTerms = sample.evaluation?.search_terms || {};
              return <article key={sample.product_id}><span className="mp-rank-number">{(previewPage - 1) * 10 + index + 1}</span><ProductImage product={product} size="thumb" fit="contain" /><div><strong>{product.name}</strong><span>{product.reference || product.vendorCode || `#${sample.product_id}`}</span>{isCloud && ['boosted', 'mid'].map((priority) => (
                searchTerms[priority]?.length ? <span className="mp-ranking-search-term-preview" key={priority} title={searchTerms[priority].join(' · ')}>{t(`merchandising.workbench.searchTerms.${priority}`)}: {searchTerms[priority].slice(0, 2).join(' · ')}{searchTerms[priority].length > 2 ? ` +${searchTerms[priority].length - 2}` : ''}</span> : null
              ))}</div><PriorityLabel factor={model.factor} copy={C} /></article>;
            })}
          </div>
          {samples.length > 0 && <div className="mp-preview-pagination"><span>{t('merchandising.workbench.previewProducts', { count: samples.length })}</span>{pages > 1 && <WorkbenchPagination page={previewPage} pageCount={pages} onChange={setPreviewPage} firstLabel={C.FIRST_PAGE} previousLabel={C.PREVIOUS_PAGE} nextLabel={C.NEXT_PAGE} lastLabel={C.LAST_PAGE} />}</div>}
          {previewBusy && <div className="mp-preview-loading" role="status" aria-live="polite"><CircularProgress size={28} thickness={4} /><strong>{C.PREVIEW_REFRESHING}</strong></div>}
        </main>
      </div>
      <Dialog className="mp-dialog" open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth aria-labelledby="mp-ranking-filter-dialog-title">
        <DialogTitle id="mp-ranking-filter-dialog-title" className="mp-dialog-title-row"><span>{t('merchandising.workbench.chooseProductFilter')}</span><IconButton aria-label={C.CLOSE} onClick={() => setFilterDialogOpen(false)}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent>
          <SearchInput
            value={filterQuery}
            onChange={setFilterQuery}
            placeholder={t('merchandising.filter.search')}
            label={t('merchandising.filter.search')}
          />
          {filterOptions.length ? <div className="mp-filter-option-grid">{filterOptions.map(({ field, descriptor, label }) => <button key={field} type="button" onClick={() => { const operator = preferredOperator(descriptor); setModel((current) => ({ ...current, conditions: appendRankingCondition(current.conditions, { field, operator, value: defaultConditionValue(descriptor, operator) }) })); setFilterDialogOpen(false); }}><strong>{label}</strong></button>)}</div> : <div className="mp-value-picker-empty">{t('merchandising.selector.empty')}</div>}
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="outlined" onClick={() => setFilterDialogOpen(false)}>{C.CANCEL}</Button></DialogActions>
      </Dialog>
      <UnsavedChangesDialog guard={unsavedGuard} />
    </div>
  );
};
RankingEditor.propTypes = { basePath: PropTypes.string.isRequired, ruleId: PropTypes.string };
RankingEditor.defaultProps = { ruleId: null };

const ProductRankingWorkspace = ({ basePath, editor, ruleId }) => {
  const enabled = useSelector((state) => state.merchandising.capability.rules);
  return (
    <CapabilityNotice enabled={enabled}>
      {editor
        ? <RankingEditor basePath={basePath} ruleId={ruleId} />
        : <RankingLibrary basePath={basePath} />}
    </CapabilityNotice>
  );
};
ProductRankingWorkspace.propTypes = { basePath: PropTypes.string.isRequired, editor: PropTypes.bool, ruleId: PropTypes.string };
ProductRankingWorkspace.defaultProps = { editor: false, ruleId: null };

export default ProductRankingWorkspace;
